<script lang="ts" setup>
    import {ref, computed, onMounted} from "vue";
    import {useStore} from "vuex";
    import {useRouter, useRoute} from "vue-router";

    import {map, values} from "lodash-es";
    import axios from "axios";

    import {generateElementSelector} from "../../helpers/html_helpers";

    import FeaturedArtists from "@songfinch/customer/components/artists/FeaturedArtists.vue";
    import StartSongButton from "@songfinch/customer/components/global/StartSongButton.vue";

    import ExclusiveLabel from "@songfinch/customer/components/artists/ExclusiveLabel.vue";
    import AtCapacityLabel from "@songfinch/customer/components/artists/AtCapacityLabel.vue";

    import TextToHtml from "@songfinch/shared/components/TextToHtml.vue";
    import analyticsEvent from "@songfinch/shared/helpers/analyticsEvent";

    import DArtistStarRating from "@songfinch/design-system/src/molecules/DArtistStarRating.vue";
    import DContainer from "@songfinch/design-system/src/atoms/DContainer.vue";
    import DText from "@songfinch/design-system/src/atoms/DText.vue";
    import DImg from "@songfinch/design-system/src/atoms/DImg.vue";
    import DButton from "@songfinch/design-system/src/atoms/DButton.vue";

    import type {ArtistPhoto, ArtistType} from "@songfinch/types";

    type ArtistSingleFeaturedArtistType = {
        name: string
        link: string
        image: ArtistPhoto
        audio: string
        available_delivery_days?: string[]
    }

    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const startSongButton = ref();
    const artist = ref<ArtistType>();
    const similarArtist = ref<{title: string, list?: ArtistSingleFeaturedArtistType[]}>();

    const atCapacity = computed(() => {
        return artist.value?.status === "at_capacity";
    });

    const artistSongs = computed(() => {
        if (!artist.value) return [];
        const id = artist.value.id;
        const image = artist.value.photo;
        const name = artist.value.artist_name;

        return map(artist.value?.songs, song => ({
            image: image,
            name: name,
            link: router.resolve({name: "ArtistSingle", params: {id}}).href,
            audio: song.audio_url,
            song_subtitle: song.genres.concat(song.occasions, song.moods).join(", "),
            song_title: song.title,
            genres: song.genres,
            occasions: song.occasions,
            media_type: "artist_single",
            player_type: "audio",
        }));
    });

    const showStartButton = computed(() => {
        return artist.value?.status === "active";
    });

    const artistLocation = computed(() => {
        if (!artist.value || !artist.value.artist_location_city) return undefined;
        let location = artist.value.artist_location_city;

        const state = artist.value.artist_location_state;
        const country = artist.value.artist_location_country;

        if (state) {
            location = location + `, ${state}`;
        }
        if (country && country !== "USA") {
            location = location + `, ${country}`;
        }
        return location;
    });

    const startSongWithArtist = async () => {
        const destination = await store.dispatch("artist/startSongWithArtist", artist.value);
        analyticsEvent("_sf_start_song_with_artist", {
            location: generateElementSelector(startSongButton.value.$el),
            destination,
            text: "Start a song with me",
            type: "button",
            artist: artist.value
        });
    };

    const getFormattedGenres = () => {
        return artist.value?.genres ? values(artist.value.genres).join(", ") : "";
    };


    onMounted(async() => {
        try {
            const res = await axios.get("/artists/" + route.params.id);
            if (!res.data.data) {
                return router.push({name: "404"});
            }
            artist.value = res.data.data;
            document.title = artist.value?.artist_name as string;
            const $list = artist.value?.similarity?.map<ArtistSingleFeaturedArtistType>(el => {
                return {
                    name: el.artist_name,
                    link: "/artists/" + el.artist_id,
                    image: el.artist_photo,
                    audio: el.artist_song?.[0]?.audio_url,
                    available_delivery_days: el.available_delivery_days
                };
            }
            );
            similarArtist.value = {
                title: `Fans of ${artist.value?.artist_name} also liked`,
                list: $list
            };
        } catch (e) {
            return router.push({name: "404"});
        }
        analyticsEvent("_sf_pageview");
    });
</script>

<template>
    <DContainer v-if="artist" display="grid">
        <DContainer class="artist-single-card" theme="dark">
            <DImg
                v-if="artist.artist_name"
                class="artist-card-image"
                :src="artist.photo.url"
                variant="card"
                src-fallback="https://media-staging.songfinch.com/static/image_placeholder.png?format=auto&width=720"
                :alt="artist.artist_name"
            />
        </DContainer>

        <DContainer class="artist-single-details">
            <DContainer v-if="artist.exclusivity || atCapacity" class="artist-single-details-badges" display="grid">
                <ExclusiveLabel v-if="artist.exclusivity"/>
                <AtCapacityLabel v-if="atCapacity"/>
            </DContainer>
            <DText variant="h-2" spacing="l" v-text="artist.artist_name"/>
            <DArtistStarRating
                v-if="artist.artist_applause_rating"
                class="@flex @justify-center @mb-2"
                theme="light"
                :rating="artist.artist_applause_rating"
                :songs-completed="artist.songs_completed"
            />
            <DText v-if="artistLocation">
                {{artistLocation}}
            </DText>
            <DText v-if="artist.genres" color="muted">
                {{getFormattedGenres()}}
            </DText>

            <DContainer v-if="artist.social_links" display="flex" class="artist-single-details-social-container">
                <template
                    v-for="soc in artist.social_links"
                    :key="soc.site"
                >
                    <DContainer class="artist-single-details-social-item">
                        <DButton 
                            :data-test-id="soc.site"
                            :href="soc.url"
                            target="_blank"
                            :aria-label="soc.site"
                            :icon="soc.site"
                            variant="icon-muted"
                        />
                    </DContainer>
                </template>
            </DContainer>

            <div v-if="showStartButton" class="@mt-6">
                <StartSongButton ref="startSongButton" data-cy="start-song-whith-artist" class="mkButton short" text="Start a song with me" @click="startSongWithArtist()"/>
            </div>
        </DContainer>
        <DContainer class="artist-single-song-list">
            <div class="artist-single-song-list-head @pb-0 tablet:@pb-2 @flex @mx-0 tablet:@-mx-5 @px-0 tablet:@px-5">
                <div class="@flex-[0_0_auto]" style="width: 64px"/>
                <div class="l8 @flex-[1_0_0%] @hidden tablet-lg:@block">
                    Title
                </div>
                <div class="l8 @flex-[1_0_0%] @hidden tablet-lg:@block @pl-8">
                    Genres & moods
                </div>
            </div>
            <div v-for="song in artistSongs" :key="song.audio" class="artist-single-song-list-table @flex @mx-0 tablet:@-mx-5 @py-4 @px-0 tablet:@px-5">
                <div class="@flex @items-center @flex-[0_0_auto] @w-full">
                    <PlayButton ref="playButton" class="@flex-[0_0_auto] @mr-4" :data="song"/>
                    <div class="@flex @flex-wrap @flex-col tablet:@flex-row @grow @items-center">
                        <div class="l3 @flex-[0_0_auto] @w-full tablet-lg:@max-w-[50%]" :title="song.song_title" v-text="song.song_title"/>
                        <div v-if="song.song_subtitle" class="p3 @text-sfc-medium-grey @flex-[0_0_auto] @w-full tablet-lg:@max-w-[50%]" :title="song.song_subtitle" v-text="song.song_subtitle"/>
                    </div>
                </div>
            </div>
        </DContainer>
        <DContainer v-if="artist?.bio" class="artist-single-bio">
            <DText variant="p-2-thicc" spacing="m">
                Artist bio
            </DText>
            <TextToHtml class="@text-sfc-dark-grey p3" :source="artist.bio"/>
        </DContainer>
    </DContainer>
    <FeaturedArtists 
        v-if="similarArtist?.list" 
        title-class="h3" 
        class="@pb-26 @pt-20 @bg-sfc-light-beige" 
        :data="similarArtist"
    />
</template>

<style scoped lang="scss">
    .artist-single-card {
        margin-block: 1rem;
        position: relative;
        justify-self: center;
        width: 333px;
        height: 333px;
        border-radius: var(--radius-20);
        overflow: hidden;

        &:after {
            position: absolute;
            width: 100%;
            height: 50%;
            content: "";
            background: linear-gradient(180deg, rgba(28, 27, 28, 0) -0%, rgba(28, 27, 28, .5) 100%);
            mix-blend-mode: multiply;
            z-index: 2;
            top: 50%;
        }
    }

    .artist-card-image {
        &.d-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            z-index: 1;
            position: absolute;
            top: 0;
            left: 0;
        }
    }

    .artist-single-details,
    .artist-single-song-list,
    .artist-single-bio {
        width: 100%;
        max-width: 800px;
        justify-self: center;
        padding-inline: 1.5rem;
        padding-block: 2rem;
    }


    .artist-single-details {
        display: grid;
        justify-content: center;
        text-align: center;
        padding-top: 0;
    }

    .artist-single-details-badges {
        row-gap: .5rem;
        margin-bottom: 1rem;
        justify-content: center;
    }


    .artist-single-details-social-container {
        align-self: flex-start;
        justify-content: center;
        align-items: center;
        margin-top: 1rem;
    }

    .artist-single-details-social-item {
        display: flex;
        align-items: center;
        margin-left: .5rem;
        padding-right: .5rem;
        border-right: 1px solid var(--soft-black-tint-8);

        &:deep(.d-link) {
            display: flex;
        }

        &:last-child {
            border-right: 0;
        }
    }

    .artist-single-song-list-head,
    .artist-single-song-list-table {
        border-bottom: 1px solid var(--soft-black-tint-8);
    }
    
    .artist-single-bio {
        margin-bottom: 3rem;
    }
</style>
